import { computed, watchEffect } from 'vue';

import type { useAttractionPageData } from './useAttractionPageData';

type PageData = Pick<ReturnType<typeof useAttractionPageData>, 'card' | 'seo'>;

const upsertMeta = (selector: string, attr: 'name' | 'property', attrValue: string, content?: string) => {
  let meta = document.head.querySelector<HTMLMetaElement>(selector);
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute(attr, attrValue);
    document.head.append(meta);
  }

  if (content) {
    meta.setAttribute('content', content);
  } else {
    meta.removeAttribute('content');
  }
};

const upsertCanonical = (href?: string) => {
  let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement('link');
    canonical.setAttribute('rel', 'canonical');
    document.head.append(canonical);
  }

  if (href) {
    canonical.setAttribute('href', href);
  } else {
    canonical.removeAttribute('href');
  }
};

/** Title, canonical, description, robots и Open Graph страницы достопримечательности. */
export const useAttractionSeo = ({ card, seo }: PageData) => {
  const coverPhotoUrl = computed(() => card.value?.photos[0]?.medium);
  const pageUrl = computed(() => seo.value?.canonical_url || window.location.href);
  // для достопримечательностей BFF присылает metatags без title/description, берём из карточки
  const pageTitle = computed(() => seo.value?.title || seo.value?.h1 || card.value?.name);
  const pageDescription = computed(() => seo.value?.description || card.value?.description);

  watchEffect(() => {
    const title = pageTitle.value;
    const description = pageDescription.value;
    const robots = seo.value?.noindex ? 'noindex, nofollow' : 'index, follow';
    const url = pageUrl.value;
    const image = coverPhotoUrl.value;

    if (title) {
      document.title = title;
    }

    upsertCanonical(url);
    upsertMeta('meta[name="description"]', 'name', 'description', description);
    upsertMeta('meta[name="robots"]', 'name', 'robots', robots);
    upsertMeta('meta[property="og:title"]', 'property', 'og:title', title);
    upsertMeta('meta[property="og:description"]', 'property', 'og:description', description);
    upsertMeta('meta[property="og:type"]', 'property', 'og:type', 'article');
    upsertMeta('meta[property="og:url"]', 'property', 'og:url', url);
    upsertMeta('meta[property="og:image"]', 'property', 'og:image', image);
    upsertMeta('meta[property="og:image:width"]', 'property', 'og:image:width', '1220');
    upsertMeta('meta[property="og:image:height"]', 'property', 'og:image:height', '600');
    upsertMeta('meta[name="twitter:card"]', 'name', 'twitter:card', 'summary_large_image');
    upsertMeta('meta[name="twitter:image"]', 'name', 'twitter:image', image);
  });
};
